import {m as motion} from 'motion/react'
import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import {tokens} from '@/theme'

import type {ReactNode} from 'react'

const variants = {
    initial: {
        opacity: 0,
        y: tokens.spacing20,
    },
    animate: {
        opacity: 1,
        y: 0,
    },
    hide: {
        opacity: 0,
    },
}

interface Props {
    children: ReactNode
    rotationSpeed?: number
}

const RotatingElements = ({children, rotationSpeed = 3}: Props) => {
    const [activeIndex, setActiveIndex] = React.useState(0)
    const [rotatingElementsRef, inView] = useInView({
        threshold: 0.5,
    })

    const childrenCount = React.Children.toArray(children).filter(Boolean).length

    React.useEffect(() => {
        if (inView) {
            let timeout: ReturnType<typeof setTimeout>

            timeout = setTimeout(() => {
                setActiveIndex((activeIndex) => (activeIndex + 1) % childrenCount)
            }, rotationSpeed * 1000)

            return () => {
                clearTimeout(timeout)
            }
        }
    }, [inView, activeIndex, childrenCount, rotationSpeed])

    return (
        <Wrapper ref={rotatingElementsRef}>
            {React.Children.map(children, (child, i) => (
                <RotatingElement
                    key={i}
                    $index={i}
                    variants={variants}
                    initial="initial"
                    animate={activeIndex === i ? 'animate' : 'hide'}
                >
                    {child}
                </RotatingElement>
            ))}
        </Wrapper>
    )
}

const Wrapper = styled.ul`
    position: relative;
    display: flex;
    padding: 0;
    margin: 0;
    overflow: hidden;
    list-style: none;
    flex-wrap: nowrap;
`

const Element = styled(motion.li)`
    flex: 1 0 100%;
    position: relative;
`

type RotatingElementProps = {
    $index: number
}

const RotatingElement = styled(Element)<RotatingElementProps>`
    left: -${(props) => props.$index * 100}%;
`

export default RotatingElements
